import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import classnames from 'classnames';
import classNames from 'classnames';

import { imageHooks, ImageSize } from '@nxte-nl/image-hooks';
import { StaticImage } from '@nxte-nl/static-image';

import {
  FeatureToggleWrapContainer,
} from '../../containers/FeatureToggleWrapContainer/FeatureToggleWrapContainer';
import { HandContainer } from '../../containers/HandContainer/HandContainer';
import { featureStateHooks } from '../../hooks/featureStateHooks/featureStateHooks';
import { optionalCallbackHooks } from '../../hooks/optionalCallbackHooks/optionalCallbackHooks';
import like from '../../images/labels/like.svg';
import star from '../../images/labels/star.svg';
import { FeatureToggleValues } from '../../types/FeatureToggles';
import { ProductCardProps, ProductCardType } from '../../types/common';
import { ApplicationSiteGroup } from '../../types/commonEnums';
import { DangerouslySetInnerHtml } from '../DangerouslySetInnerHtml/DangerouslySetInnerHtml';
import { Link, LinkType } from '../Link/Link';
import { Typography } from '../Typography/Typography';
import { Chevron as ChevronIcon, ChevronColor } from '../icons/Chevron/Chevron';
import styles from './ProductCard.module.css';

const labelIcons = {
  like: like,
  star: star,
};

const messages = defineMessages({
  freeLabel: {
    id: 'src.components.ProductCard.freeLabel',
    defaultMessage: 'Free',
  },
  labelIconAlt: {
    id: 'src.components.ProductCard.labelIconAlt',
    defaultMessage: 'Label icon',
  },
});

export type Props = ProductCardProps & {
  id: string | null;
};

export const hooks = {
  useLinkType(disablePath: boolean, isExsternalLink?: boolean): LinkType {
    return React.useMemo(() => {
      if (disablePath) {
        return LinkType.Span;
      }
      if (isExsternalLink) {
        return LinkType.ExternalHref;
      }
      return LinkType.Href;
    }, [disablePath, isExsternalLink]);
  },
};

export const ProductCard = React.memo((props: Props) => {
  const {
    id,
    title,
    path,
    disablePath = false,
    description,
    backgroundColor,
    showHand,
    cardImage,
    backgroundImage,
    className,
    onClick,
    cardType,
    showFreeLabel,
    isReorder,
    isExsternalLink,
    showShadow,
    label,
  } = props;
  const intl = useIntl();

  const backgroundImageHref = imageHooks.useSize(ImageSize.XL, backgroundImage?.url.href ?? '');
  const cardImageHref = imageHooks.useSize(ImageSize.M, cardImage?.url.href ?? '');

  const handleClick = optionalCallbackHooks.useHandleClickWithId(id, onClick);
  const type = hooks.useLinkType(disablePath, isExsternalLink);

  const { isHandV1 } = featureStateHooks.useFeatureToggles();

  return (
    <Link
      type={type}
      onClick={handleClick}
      to={path ?? '/'}
      className={classnames(
        styles.productCard,
        { [styles.withHand]: cardType == ProductCardType.L,
          [styles.withFreeLabel]: showFreeLabel,
          [styles.withHandV1]: isHandV1,
        },
        className,
      )}
      isBlock={true}
    >
      <>
        <div>
          {cardType == ProductCardType.L ? (
            <>
              {backgroundImageHref !== '' && (
                <div
                  className={styles.backgroundImage}
                  style={{
                    backgroundImage: `url("${backgroundImageHref}")`,
                  }}
                />
              )}

              {backgroundColor && (
                <div
                  className={styles.backgroundColor}
                  style={{
                    backgroundColor: backgroundColor?.color,
                    opacity: backgroundColor?.opacity,
                  }}
                />
              )}

              {cardImageHref !== '' && (
                <div className={styles.cardWithHandContainer}>
                  <div
                    className={classNames(styles.cardContainer, showShadow && styles.cardShadow)}
                    style={{
                      backgroundImage: `url("${cardImageHref}")`,
                    }}
                  >
                    {showHand && (
                      <HandContainer />
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            cardType === ProductCardType.S && (
              <div className={styles.cardWithBackgroundColorContainer}>
                <div
                  className={styles.backgroundColor}
                  style={{
                    backgroundColor: backgroundColor?.color,
                    opacity: backgroundColor?.opacity,
                  }}
                />

                {label && (
                  <div
                    className={classNames(
                      styles.label,
                      styles[label.color],
                    )}
                  >
                    { label.icon
                    && <StaticImage
                      className={styles.labelIcon}
                      src={labelIcons[label.icon]}
                      alt={intl.formatMessage(messages.labelIconAlt)}
                      width={14}
                    />
                    }

                    <Typography component='span' className={styles.labelTitle}>
                      {label.title}
                    </Typography>
                  </div>
                )}

                {cardImageHref && (
                  <div
                    className={classNames(styles.cardWithBackgroundColor, {
                      [styles.cardShadow]: showShadow,
                    })}
                    style={{
                      backgroundImage: `url("${cardImageHref}")`,
                    }}
                  />
                )}
              </div>
            )
          )}

          <div
            className={classnames(styles.infoContainer, {
              [styles.withHand]: cardType === ProductCardType.L,
            })}
          >
            <Typography component='p' variant='h4' className={styles.title}>
              {title}
            </Typography>

            {cardType === ProductCardType.L && description && (

              <DangerouslySetInnerHtml
                variant={'bodySubText1'}
                size={isReorder ? 'l' : undefined}
                className={styles.descriptionText}
                dangerousHtml={description}
              />
            )}

            {showFreeLabel && (
              <Typography className={styles.freeLabel} variant='body1'>
                <FormattedMessage {...messages.freeLabel} />
              </Typography>
            )}
          </div>
        </div>
        <FeatureToggleWrapContainer>
          {({ isGCSOrangeColor }: FeatureToggleValues) => (
            <div className={classnames(
              styles.navButton,
              {
                [styles.navButtonAlt]: CONFIG.applicationSiteGroup === ApplicationSiteGroup.GCS
                && isGCSOrangeColor,
              },
            )}>
              <ChevronIcon
                className={classnames(styles.navButtonIcon, styles.navButtonChevronIcon)}
                fill={ChevronColor.accentWhite}
                variant='right'
                width={15}
                height={15}
              />
            </div>
          )}
        </FeatureToggleWrapContainer>
      </>
    </Link>
  );
});

ProductCard.displayName = 'ProductCard';
